import Bugsnag from "@bugsnag/js";

import { ApiError, isApiError } from "@config/apiClient";
import { REACT_QUERY_ERROR } from "@config/constants";
import { DisconnectedError } from "@app-types/errors";
import { normalizeApiPath } from "@utils/strings.util";

export function useBugsnagErrorHandler() {
  const notifyBugsnag = (error: unknown, context?: string) => {
    if (!Bugsnag.isStarted()) return;
    // skip if token expired
    if ((error as ApiError)?.response?.status === 401) return;
    // skip expected disconnection (403)
    if (error instanceof DisconnectedError) return;

    let normalizedError: Error;

    if (isApiError(error)) {
      normalizedError = new Error(`ApiError: ${error.message} [${error.config?.url}]`);
      (normalizedError as any).response = error.response;
    } else if (error instanceof Error) {
      normalizedError = error;
    } else {
      normalizedError = new Error(typeof error === "string" ? error : "Unknown error");
    }

    normalizedError.name = REACT_QUERY_ERROR; // <-- important
    (normalizedError as any).__fromReactQuery = true; // optional extra marker

    Bugsnag.notify(normalizedError, (event) => {
      // Call site only; raw body lives under `http` below.
      event.addMetadata("react-query", { callSite: context });

      // Structured HTTP context for the Bugsnag dashboard's HTTP tab and
      // for the onError noise filter to read via event.getMetadata("http").
      // `event.context` shows the URL in the dashboard list view.
      if (isApiError(error) && error.response) {
        event.context = error.config?.url;
        event.addMetadata("http", {
          url: error.config?.url,
          status: error.response.status,
          body: error.response.data,
          method: error.config?.method,
        });

        // Group by status + UUID-normalized path so 400s do not collapse
        // into one "Bad request error (400)" mega-group keyed on the message.
        event.groupingHash = `${error.response.status} ${normalizeApiPath(error.config.url)}`;
      }
    });
  };

  return { notifyBugsnag };
}
